/**
 * Full-screen game shell overlays: loading indicator, home/title screen, the
 * New Game config modal, and the in-game Pause menu. Pure DOM, styled by
 * hud.css (.tbg-*). Each builder takes plain callbacks so main.ts owns the
 * actual boot/quit/save wiring.
 */
import { icon, type IconName } from "./icons";

function h(tag: string, cls?: string, html?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html !== undefined) e.innerHTML = html;
  return e;
}

function mbtn(icn: IconName, label: string, kind: string, on: () => void): HTMLElement {
  const b = h("button", `tbg-mbtn ${kind}`, `<span class="i">${icon(icn, 18)}</span><span>${label}</span>`);
  b.addEventListener("click", on);
  return b;
}

// ---- loading -------------------------------------------------------------
let loadingEl: HTMLElement | null = null;
export function showLoading(msg: string): (m: string) => void {
  if (!loadingEl) {
    loadingEl = h("div", "tbg-screen tbg-loading",
      `<div class="tbg-load-box"><div class="tbg-spinner"></div><div class="tbg-load-msg"></div></div>`);
    document.body.appendChild(loadingEl);
  }
  const m = loadingEl.querySelector(".tbg-load-msg") as HTMLElement;
  m.textContent = msg;
  return (next: string) => { m.textContent = next; };
}
export function hideLoading(): void {
  loadingEl?.remove();
  loadingEl = null;
}

// ---- home / title --------------------------------------------------------
export function showHome(o: { hasContinue: boolean; onContinue: () => void; onNew: () => void; onLoad: () => void }): void {
  const s = h("div", "tbg-screen tbg-home",
    `<div class="tbg-home-inner">
       <div class="tbg-brand">
         <div class="tbg-kicker">// hex strategy</div>
         <h1 class="tbg-title">The Boring Game</h1>
         <div class="tbg-tagline">build · gather · conquer the hexlands</div>
       </div>
       <div class="tbg-menu"></div>
       <div class="tbg-foot">v0 · drag to box-select · right-click to command</div>
     </div>`);
  const menu = s.querySelector(".tbg-menu") as HTMLElement;
  if (o.hasContinue) menu.appendChild(mbtn("play", "Continue", "primary", o.onContinue));
  menu.appendChild(mbtn("flag", "New Game", o.hasContinue ? "" : "primary", o.onNew));
  menu.appendChild(mbtn("folderOpen", "Load Game", "", o.onLoad));
  document.body.appendChild(s);
}

// ---- new game config -----------------------------------------------------
export interface NewGamePlayer { civ: string; kind: "human" | "bot"; team: number; }

const MAX_OPPONENTS = 7;
const MAX_TEAMS = 8;

/** Custom chevron so the native UA arrow doesn't crowd the right edge. */
const SELECT_CHEVRON =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23a8a8b2' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")";

/** Dark-theme styling for the <select> dropdowns. We drop the native appearance
 *  and draw our own chevron inset from the right edge, with matching right
 *  padding so the value text never collides with the arrow. */
function styleSelect(el: HTMLElement): void {
  Object.assign(el.style, {
    background: "#15151a", color: "#e7e7ec", font: "inherit",
    border: "1px solid rgba(245,245,247,0.12)", borderRadius: "8px",
    padding: "8px 34px 8px 12px", minWidth: "0", cursor: "pointer",
    appearance: "none", backgroundImage: SELECT_CHEVRON,
    backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center",
  } as Partial<CSSStyleDeclaration> & { appearance: string });
  // Safari/older engines
  (el.style as unknown as Record<string, string>).webkitAppearance = "none";
}

export function showNewGame(o: {
  defaults: { seed: number; cols: number; rows: number };
  civs: { id: string; name: string }[];
  onStart: (cfg: { seed: number; cols: number; rows: number; players: NewGamePlayer[] }) => void;
  onCancel: () => void;
}): void {
  // The title-menu button that launched this dialog remains the logical focus
  // target when configuration is dismissed. Capture it before modal.focus().
  const invoker = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const civOptions = (sel: string) =>
    o.civs.map((c) => `<option value="${c.id}"${c.id === sel ? " selected" : ""}>${c.name}</option>`).join("");
  const teamOptions = (sel: number) =>
    Array.from({ length: MAX_TEAMS }, (_, i) => `<option value="${i + 1}"${i + 1 === sel ? " selected" : ""}>Team ${i + 1}</option>`).join("");
  const firstCiv = o.civs[0]?.id ?? "";
  let humanTeam = 1;
  // start with one opponent on its own team (free-for-all), a different civ
  const bots: { civ: string; team: number }[] = [{ civ: o.civs[1]?.id ?? firstCiv, team: 2 }];

  const s = h("div", "tbg-screen tbg-modal-bg tbg-newgame",
    `<div class="tbg-modal" role="dialog" aria-modal="true" aria-labelledby="ng-title" tabindex="-1">
       <div class="tbg-modal-head"><span class="i">${icon("flag", 18)}</span>
         <div><div class="tbg-modal-title" id="ng-title">New Game</div><div class="tbg-modal-sub">configure your match</div></div></div>
       <label class="tbg-field"><span>Seed</span>
         <div class="tbg-seed"><input id="ng-seed" type="number" value="${o.defaults.seed}">
           <button id="ng-rand" title="Random seed">${icon("shuffle", 15)}</button></div></label>
       <div class="tbg-grid2">
         <label class="tbg-field"><span>Columns</span><input id="ng-cols" type="number" min="16" max="400" value="${o.defaults.cols}"></label>
         <label class="tbg-field"><span>Rows</span><input id="ng-rows" type="number" min="16" max="400" value="${o.defaults.rows}"></label>
       </div>
       <div class="tbg-field"><span>You — civ &amp; team</span>
         <div style="display:flex;gap:6px">
           <select id="ng-civ">${civOptions(firstCiv)}</select>
           <select id="ng-team">${teamOptions(humanTeam)}</select>
         </div></div>
       <div class="tbg-field"><span>Opponents — civ &amp; team (same team = allies)</span>
         <div id="ng-bots" style="display:flex;flex-direction:column;gap:6px"></div>
         <button id="ng-addbot" type="button" style="margin-top:8px;align-self:flex-start;background:rgba(255,255,255,0.06);color:#cfcfd6;border:1px dashed rgba(245,245,247,0.18);border-radius:8px;padding:7px 12px;cursor:pointer;font:inherit">+ Add opponent</button>
       </div>
       <div class="tbg-modal-actions">
         <button class="tbg-mbtn ghost" id="ng-cancel"><span>Cancel</span></button>
         <button class="tbg-mbtn primary" id="ng-start"><span class="i">${icon("play", 16)}</span><span>Start</span></button>
       </div>
     </div>`);

  const seed = s.querySelector("#ng-seed") as HTMLInputElement;
  const cols = s.querySelector("#ng-cols") as HTMLInputElement;
  const rows = s.querySelector("#ng-rows") as HTMLInputElement;
  const civSel = s.querySelector("#ng-civ") as HTMLSelectElement;
  const teamSel = s.querySelector("#ng-team") as HTMLSelectElement;
  const botsEl = s.querySelector("#ng-bots") as HTMLElement;
  const addBtn = s.querySelector("#ng-addbot") as HTMLButtonElement;
  styleSelect(civSel); civSel.style.flex = "2";
  styleSelect(teamSel); teamSel.style.flex = "0 0 118px";
  teamSel.addEventListener("change", () => { humanTeam = Number(teamSel.value); });

  const renderBots = () => {
    botsEl.innerHTML = "";
    if (bots.length === 0) {
      botsEl.appendChild(h("div", "", "<span style='color:#8a8a92;font-size:12px'>No opponents — sandbox / free build.</span>"));
    }
    bots.forEach((bot, i) => {
      const row = h("div", "", `<select class="bc">${civOptions(bot.civ)}</select><select class="bt">${teamOptions(bot.team)}</select><button title="Remove opponent" type="button">${icon("x", 14)}</button>`);
      Object.assign(row.style, { display: "flex", gap: "6px", alignItems: "center" } as Partial<CSSStyleDeclaration>);
      const civsel = row.querySelector(".bc") as HTMLSelectElement;
      const teamsel = row.querySelector(".bt") as HTMLSelectElement;
      styleSelect(civsel); civsel.style.flex = "2";
      styleSelect(teamsel); teamsel.style.flex = "0 0 118px";
      civsel.addEventListener("change", () => { bots[i].civ = civsel.value; });
      teamsel.addEventListener("change", () => { bots[i].team = Number(teamsel.value); });
      const rm = row.querySelector("button") as HTMLButtonElement;
      Object.assign(rm.style, {
        display: "grid", placeItems: "center", width: "34px", height: "34px",
        background: "rgba(255,90,90,0.10)", color: "#e0746e", cursor: "pointer",
        border: "1px solid rgba(255,90,90,0.25)", borderRadius: "8px", flex: "0 0 auto",
      } as Partial<CSSStyleDeclaration>);
      rm.addEventListener("click", () => { bots.splice(i, 1); renderBots(); addBtn.focus(); });
      botsEl.appendChild(row);
    });
    addBtn.style.display = bots.length >= MAX_OPPONENTS ? "none" : "";
  };
  renderBots();
  addBtn.addEventListener("click", () => {
    if (bots.length < MAX_OPPONENTS) {
      bots.push({ civ: o.civs[bots.length % o.civs.length]?.id ?? firstCiv, team: bots.length + 2 });
      renderBots();
    }
  });

  const clamp = (v: number, lo: number, hi: number, dflt: number) =>
    Number.isFinite(v) && v > 0 ? Math.max(lo, Math.min(hi, Math.round(v))) : dflt;
  (s.querySelector("#ng-rand") as HTMLElement).addEventListener("click", () => {
    seed.value = String(Math.floor(Math.random() * 1_000_000));
  });
  const home = document.querySelector(".tbg-home") as HTMLElement | null;
  home?.setAttribute("inert", "");
  home?.setAttribute("aria-hidden", "true");
  const close = (next: () => void, restoreFocus = false) => {
    s.remove();
    home?.removeAttribute("inert");
    home?.removeAttribute("aria-hidden");
    if (restoreFocus && invoker?.isConnected) invoker.focus();
    next();
  };
  (s.querySelector("#ng-cancel") as HTMLElement).addEventListener("click", () => close(o.onCancel, true));
  (s.querySelector("#ng-start") as HTMLElement).addEventListener("click", () => {
    const cfg: { seed: number; cols: number; rows: number; players: NewGamePlayer[] } = {
      seed: Math.round(Number(seed.value)) || 1337,
      cols: clamp(Number(cols.value), 16, 400, 150),
      rows: clamp(Number(rows.value), 16, 400, 90),
      players: [
        { civ: civSel.value, kind: "human", team: humanTeam },
        ...bots.map((b): NewGamePlayer => ({ civ: b.civ, kind: "bot", team: b.team })),
      ],
    };
    close(() => o.onStart(cfg));
  });
  document.body.appendChild(s);
  const modal = s.querySelector(".tbg-modal") as HTMLElement;
  s.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close(o.onCancel, true);
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = [...s.querySelectorAll<HTMLElement>("button:not([disabled]), input:not([disabled]), select:not([disabled])")];
    const first = focusable[0], last = focusable.at(-1);
    if (!first || !last) return;
    if (!focusable.includes(document.activeElement as HTMLElement)) { event.preventDefault(); (event.shiftKey ? last : first).focus(); }
    else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  modal.focus();
}

// ---- pause menu ----------------------------------------------------------
export function showPause(o: {
  onResume: () => void; onSave: () => void; onLoad: () => void; onQuit: () => void;
  debugOn: () => boolean; onToggleDebug: () => void;
  camAngle: () => number; setCamAngle: (v: number) => void;
}): void {
  if (document.querySelector(".tbg-pause")) return;
  const s = h("div", "tbg-screen tbg-modal-bg tbg-pause",
    `<div class="tbg-modal tbg-pause-modal">
       <div class="tbg-modal-head"><span class="i">${icon("pause", 18)}</span>
         <div><div class="tbg-modal-title">Paused</div><div class="tbg-modal-sub">the hexlands wait</div></div></div>
       <div class="tbg-menu"></div>
       <div class="tbg-opt-head">// options</div>
       <button class="tbg-mbtn ghost" id="pz-dbg"></button>
       <label class="tbg-field tbg-tilt"><span>Camera tilt <b id="pz-tiltv"></b></span>
         <input id="pz-tilt" type="range" min="22" max="82" step="1" value="${o.camAngle()}"></label>
     </div>`);
  const menu = s.querySelector(".tbg-menu") as HTMLElement;
  menu.append(
    mbtn("play", "Resume", "primary", o.onResume),
    mbtn("save", "Save game", "", o.onSave),
    mbtn("folderOpen", "Load game", "", o.onLoad),
    mbtn("logOut", "Quit to menu", "danger", o.onQuit),
  );
  const dbg = s.querySelector("#pz-dbg") as HTMLElement;
  const syncDbg = () => { dbg.innerHTML = `<span class="i">${icon("sliders", 16)}</span><span>Debug overlay · ${o.debugOn() ? "on" : "off"} (F3)</span>`; };
  syncDbg();
  dbg.addEventListener("click", () => { o.onToggleDebug(); syncDbg(); });
  const tilt = s.querySelector("#pz-tilt") as HTMLInputElement;
  const tiltV = s.querySelector("#pz-tiltv") as HTMLElement;
  const syncTilt = () => { tiltV.textContent = `${o.camAngle()}°`; };
  syncTilt();
  tilt.addEventListener("input", () => { o.setCamAngle(Number(tilt.value)); syncTilt(); });
  document.body.appendChild(s);
}
export function hidePause(): void {
  document.querySelector(".tbg-pause")?.remove();
}
